import Modal from "@/components/base/Modal";
import { cn } from "@/utils/cn";

interface ConfirmDeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description: string;
  confirmLabel?: string;
  /**
   * Message bloquant : si renseigné, la suppression est empêchée car des
   * éléments liés existent. On invite l'utilisateur à les traiter d'abord
   * afin d'éviter toute donnée orpheline.
   */
  blockedMessage?: string;
}

/**
 * Confirmation obligatoire avant toute suppression d'un élément.
 * La suppression n'est jamais déclenchée au premier clic.
 * Si des éléments liés existent (`blockedMessage`), le bouton Supprimer est
 * neutralisé et un avertissement clair est affiché.
 */
export default function ConfirmDeleteModal({
  open,
  onClose,
  onConfirm,
  title = "Confirmer la suppression",
  description,
  confirmLabel = "Supprimer",
  blockedMessage,
}: ConfirmDeleteModalProps) {
  const blocked = Boolean(blockedMessage);

  return (
    <Modal open={open} onClose={onClose} size="sm" title={title}>
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
            blocked ? "bg-accent-100 text-accent-700" : "bg-primary-100 text-primary-700"
          )}
        >
          <i className={cn(blocked ? "ri-links-line" : "ri-delete-bin-line", "text-xl")} aria-hidden="true" />
        </span>
        <div className="min-w-0 pt-0.5">
          <p className="text-sm leading-relaxed text-foreground-700">{description}</p>
          {blocked && (
            <p className="mt-3 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-900">
              <i className="ri-alert-line mt-0.5 shrink-0 text-base text-accent-600" aria-hidden="true" />
              <span>{blockedMessage}</span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
        >
          {blocked ? "Fermer" : "Annuler"}
        </button>
        {!blocked && (
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            {confirmLabel}
          </button>
        )}
      </div>
    </Modal>
  );
}